import React, {useContext} from 'react'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'
import {HotelCardContext} from 'components/HotelCardContext'

import {Divider} from '@vio/atlas/Divider'
import {Box} from '@vio/atlas/helpers/Box'
import {cssTheme} from '@vio/atlas/themes'

interface Props {
  /** Callback fired when clicking on the card */
  onClick?: (e: React.SyntheticEvent) => void
  /** The component to render for the gallery */
  gallerySlot: React.ReactNode
  /** The component to render for the watch button */
  watchButtonSlot: React.ReactNode
  /** The component to render for the property details */
  propertySummarySlot: React.ReactNode
  /** The component to render extra property details details */
  basicPropertyDetailsSlot: React.ReactNode
  /** The component to render if there are matching filters */
  filtersMatchSlot?: React.ReactNode
  /** The component to render offers */
  offersSlot?: React.ReactNode
  /** Is the top offer member deal */
  isTopOfferMemberDeal: boolean
  /** Will add a border to highlight the anchor */
  isAnchor?: boolean
  /** Compact hotel card when dates are UA  */
  isCompactHotelCard?: boolean
  /** The component to render for the anchor label */
  anchorLabelSlot?: React.ReactNode
}

export const TOP_SECTION_MIN_HEIGHT = 182
const COMPACT_CARD_MIN_HEIGHT = 134
export const GALLERY_WIDTH = 128

const TopSection = styled.section`
  display: flex;
  flex-direction: column;
  border-radius: ${cssTheme.layout.radius.md};
  overflow: hidden;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  border: 1px solid;
  border-color: ${cssTheme.colors.border.brand.tertiary};
  border-bottom-color: ${cssTheme.colors.background.brand.secondary};

  &.--compact-hotel-card {
    border-color: ${cssTheme.colors.border.neutral.secondary};
    border-bottom-color: ${cssTheme.colors.border.neutral.secondary};
  }
  &.--top-offer-member-deal {
    border-color: ${cssTheme.colors.border.brand.tertiary};
  }

  &.--with-selected-label {
    border-radius: 0;
  }
`

const PriceWatchButtonWrapper = styled.div`
  position: absolute;
  top: 7px;
  left: 7px;
  z-index: 1;
`

/**
 * Pure presentational component for the base layout of the mobile hotel card
 * Do NOT access any context/store from here.
 */
const HotelCardLayoutComponent = ({
  onClick,
  gallerySlot,
  basicPropertyDetailsSlot,
  propertySummarySlot,
  watchButtonSlot,
  filtersMatchSlot,
  offersSlot,
  isTopOfferMemberDeal,
  isAnchor = false,
  isCompactHotelCard = false,
  anchorLabelSlot
}: Props) => {
  const {hotelId} = useContext(HotelCardContext)

  const withAnchorStyle = isAnchor && !isCompactHotelCard
  const withSelectedLabel = withAnchorStyle && !!anchorLabelSlot
  const anchorOutlineColor = withSelectedLabel
    ? 'border.interactive.primary'
    : 'hotelCard.special.border'

  return (
    <Box
      backgroundColor="background.neutral.primary"
      borderRadius="md"
      {...(isAnchor && {
        minHeight: `${COMPACT_CARD_MIN_HEIGHT}px`,
        outlineWidth: 's100',
        outlineOffset: '-1px',
        outlineStyle: 'solid',
        outlineColor: withAnchorStyle
          ? anchorOutlineColor
          : 'border.neutral.secondary'
      })}
    >
      {withSelectedLabel && anchorLabelSlot}
      <TopSection
        className={cx(
          isCompactHotelCard && '--compact-hotel-card',
          !isCompactHotelCard &&
            isTopOfferMemberDeal &&
            '--top-offer-member-deal',
          withSelectedLabel && '--with-selected-label'
        )}
      >
        <div
          role="button"
          tabIndex={0}
          onClick={onClick}
          onKeyDown={onClick}
          id={`HotelCard-${hotelId}`}
        >
          <Box
            display="flex"
            position="relative"
            minHeight={`${isCompactHotelCard ? COMPACT_CARD_MIN_HEIGHT : TOP_SECTION_MIN_HEIGHT}px`}
          >
            <Box width={`${GALLERY_WIDTH}px`}>{gallerySlot}</Box>
            <Box
              overflow="hidden"
              paddingTop="s400"
              paddingBottom="s300"
              paddingLeft="s300"
              paddingRight="s300"
              width="100%"
            >
              {propertySummarySlot}
            </Box>
            <PriceWatchButtonWrapper>{watchButtonSlot}</PriceWatchButtonWrapper>
          </Box>

          {basicPropertyDetailsSlot && (
            <>
              <Divider />
              {basicPropertyDetailsSlot}
            </>
          )}

          {filtersMatchSlot && <>{filtersMatchSlot}</>}
        </div>
      </TopSection>
      {offersSlot}
    </Box>
  )
}

export const HotelCardLayout = React.memo(HotelCardLayoutComponent)
